import type { WorkflowConfig } from './config';
import type { WorkflowRule } from './rules';

/**
 * Which form fields are filled from the connected scanner.
 *
 * Read from the RULES, not from `postRules`, and that is the whole point of this module.
 *
 * `postRules.deviceDemoFields` used to carry it, and the capture screen read it straight off the
 * workflow. Then `strip()` on the server began folding the legacy flags into `rules` on read — which was
 * right, one mechanism instead of two — and it DELETES `postRules` on the way past. The capture screen
 * went on reading `wf.postRules.deviceDemoFields`, which from that day forward was always `undefined`.
 * It still type-checked, because the field is still declared on the config; it simply never had a value.
 *
 * The visible symptom was make / model / serial sitting blank on the SWFT form. They are `required`, so
 * the operator typed them — and a typed value is what the EBTS then carries, because the folded rule only
 * fires `when: { is: 'empty' }`. Annapolis Fingerprinting's TCNs show it: submissions before the fold
 * carry their registered serial `005022636G2022` exactly, and later ones carry a truncated `005022636`
 * and a hand-typed `S234324234242432`.
 *
 * Deriving from the rules means the form and the EFT builder read ONE description of the same intent, so
 * this cannot drift apart again — and a hand-written rule that seeds a field from the scanner gets the
 * form prefill for free, without anyone having to know `postRules` existed.
 */

/** The scanner properties a rule may read. These names are the builder's, from `postRulesToRules`. */
const DEVICE_PROPS = {
  device_make: 'make',
  device_model: 'model',
  device_serial: 'serial',
} as const;

export interface DeviceDemoTargets {
  make?: string;
  model?: string;
  serial?: string;
}

/** The last field a rule writes to, which is the target the chain lands on. */
function writtenField(rule: WorkflowRule): string | undefined {
  for (let i = rule.ops.length - 1; i >= 0; i--) {
    const op = rule.ops[i];
    if (op && op.op === 'write' && op.field) return op.field;
  }
  return undefined;
}

/**
 * Form fields seeded from the scanner, by scanner property.
 *
 * A rule qualifies when it READS one of the scanner properties and WRITES to a form field. Its condition
 * is not inspected: the folded rules carry `when: { is: 'empty' }` so the builder does not overwrite a
 * correction, and the screen enforces the same thing itself by only filling blanks.
 */
export function deviceDemoTargets(rules: readonly WorkflowRule[] | undefined): DeviceDemoTargets {
  const out: DeviceDemoTargets = {};
  for (const rule of rules ?? []) {
    if (rule.enabled === false) continue;
    const prop = DEVICE_PROPS[rule.read.field as keyof typeof DEVICE_PROPS];
    if (!prop) continue;
    const target = writtenField(rule);
    // First rule wins: the converted flags go first, and a later hand-written rule is a second opinion.
    if (target && !out[prop]) out[prop] = target;
  }
  return out;
}

/**
 * The same, for a whole workflow.
 *
 * `postRules` is still consulted as a fallback. It is gone from anything the API serves, but a workflow
 * read from somewhere that has not folded it — a fixture, an export, a future caller — should not quietly
 * lose the behaviour a second time.
 */
export function workflowDeviceFields(wf: WorkflowConfig | undefined): DeviceDemoTargets {
  const fromRules = deviceDemoTargets(wf?.rules);
  if (fromRules.make || fromRules.model || fromRules.serial) return fromRules;
  const legacy = wf?.postRules?.deviceDemoFields;
  if (!legacy) return {};
  return {
    ...(legacy.make ? { make: legacy.make } : {}),
    ...(legacy.model ? { model: legacy.model } : {}),
    ...(legacy.serial ? { serial: legacy.serial } : {}),
  };
}
